/**
 * Numérotation des pièces — SPEC §30.7 : séquences CONTINUES, par caisse et
 * PAR TYPE de pièce (tickets, factures, clôtures, titres = séquences distinctes),
 * JAMAIS remises à zéro (y compris au changement d'exercice).
 * La persistance du dernier numéro appartient au produit (SQLite) ; ici :
 * l'incrément, le formatage et le contrôle de continuité (Inspecteur / archive).
 */

/**
 * Préfixes par type de séquence — SPEC §30.7.1 : `T` (ticket), `F` (facture directe ;
 * facture a posteriori/acompte/**d'avoir B2B** = MÊME séquence facture), assemblés en
 * `<PRÉFIXE>-S<magasin>R<caisse>-<seq>` ; les autres préfixes sont des conventions éditeur documentées ici.
 * ⚠️ DEUX SÉRIES DE PIÈCES, UNE CHAÎNE : les tickets (et ce qui les corrige — avoirs, annulations, justificatifs) et les
 * factures ont chacun leur numérotation continue (CGI art. 242 nonies A pour les factures) ; le rang dans la chaîne de
 * signature de la caisse (`seq_no`) les réunit, et se déduit des deux (`chainRankOf`).
 * `justificatif` : seule séquence où les discontinuités sont tolérées (§30.7.1).
 * `factureProforma` : séquence DISSOCIÉE de celle des factures (§30.7.1).
 * ⚠️ **Titres retail** (§85 : « les compteurs de titres … chacun est une séquence par caisse ») :
 * l'avoir/chèque-cadeau/acompte remis au client sont des INSTRUMENTS distincts (pas la facture d'avoir B2B),
 * chacun avec sa **séquence continue par caisse** et son préfixe parlant — `AV`/`CC`/`AC` — pour que
 * l'écran détecte le type **au scan** par le préfixe (§333). Le format est libre (NF525 n'impose que la
 * continuité par caisse) ; on réutilise le format `S<magasin>R<caisse>` des pièces → identité de code
 * **globalement unique** (partage multi-magasins), le GLN de la pièce/du titre restant la vérité de tri BO.
 * `titre` (`TI`) reste le préfixe générique (produits sans typage au préfixe).
 */
export const SEQUENCE_PREFIXES = {
  ticket: 'T',
  facture: 'F',
  factureProforma: 'FP',
  note: 'N',
  justificatif: 'JP',
  titre: 'TI',
  titreAvoir: 'AV',
  titreCadeau: 'CC',
  titreAcompte: 'AC',
  // ⚠️ `BF` = BON FIDÉLITÉ. Il a sa propre séquence CONTINUE comme les autres titres (code-barres
  //    scannable, unique réseau, continuité contrôlable) — mais ce n'est PAS un titre au sens fiscal :
  //    personne ne l'a payé, il vaut une REMISE. C'est `core-vouchers.natureOfType` qui tranche, jamais
  //    le préfixe. Réf. CONFORMITE-NF525 §2.3ter.
  titreFidelite: 'BF',
  // ⚠️ CLE FRANCAISE CONSERVEE, DELIBEREMENT (regle de langue, CLAUDE.md) : les cles de cette table
  // NE SONT PAS des identifiants mais un VOCABULAIRE — elles circulent comme chaînes
  // (`formatPieceNumber('cloture', …)`). En renommer une seule rendrait la table à moitié anglaise
  // et casserait les appelants en SILENCE (le typage ne suit pas une chaîne littérale). Le
  // vocabulaire `SequenceKind` se traduit EN BLOC ou pas du tout — chantier à part.
  cloture: 'Z',
  // ⚠️ Compteur GÉNÉRIQUE du cycle atelier/SERVICE (préfixe `SR`, figé §arbitrage 2026-07-29) : la
  // « retouche textile » n'est que la déclinaison RETAIL — d'autres secteurs le déclinent (réparation
  // d'écran, préparation d'un gâteau…). Le cœur PARTAGÉ ne code donc pas « retouche » mais `service`.
  service: 'SR',
} as const;

export type SequenceKind = keyof typeof SEQUENCE_PREFIXES;

/**
 * LES PRÉFIXES QU'UNE SÉRIE A PORTÉS AVANT LE SIEN — lus, jamais plus émis.
 * ⚠️ 🔴 ILS NE SE RENOMMENT PAS (Forme J) : un numéro entre dans la charge SIGNÉE de sa pièce. Les factures ont été
 *    numérotées `FT-` avant `F-` ; une pièce scellée sous l'ancien préfixe reste une facture, et sa série continue
 *    sous le nouveau — c'est ce que lisent la reprise des compteurs et le rang de chaîne.
 */
export const FORMER_SEQUENCE_PREFIXES: Readonly<Partial<Record<SequenceKind, readonly string[]>>> = {
  facture: ['FT'],
};

/** Les deux séries de pièces d'une caisse : les tickets et ce qui les corrige (`PIECE`), les factures (`FACTURE`). */
export type PieceSeries = 'PIECE' | 'FACTURE';

/**
 * LE RANG D'UNE PIÈCE DANS LA CHAÎNE DE SA CAISSE — et le numéro qu'elle prend dans sa série.
 * ⚠️ LE RANG SE DÉDUIT DES DEUX COMPTEURS, il n'en a pas un à lui : chaque pièce avance exactement UNE des deux séries,
 *    donc le rang de la suivante vaut leur somme + 1. Une caisse sans facture garde ainsi rang = numéro de ticket, et une
 *    tablette de remplacement, qui reprend les deux compteurs auprès du serveur, reprend aussi son rang — sans compteur
 *    de plus à faire voyager.
 */
export function chainRankOf(last: Readonly<Record<PieceSeries, number>>, series: PieceSeries): { chainRank: number; documentSeq: number } {
  for (const v of [last.PIECE, last.FACTURE]) {
    if (!Number.isInteger(v) || v < 0) throw new Error(`core-nf525: compteur de série invalide (${v})`);
  }
  return { chainRank: last.PIECE + last.FACTURE + 1, documentSeq: last[series] + 1 };
}

/**
 * LE PRÉFIXE DE REGISTRE D'UNE REPRISE — `LEGACY-C<n>`.
 *
 * POURQUOI IL EXISTE : une reprise pose un registre DÉDIÉ pour que la chaîne de signature d'un
 * historique repris ne se mélange jamais à celle d'une caisse en service (§26.6bis). C'est la
 * séparation par registre qui rend la chose saine.
 *
 * ⚠️ 🔴 IL NE SE RENOMME PAS (Forme J) : il est PERSISTÉ dans des pièces signées et append-only. Ce
 * qu'on centralise ici est sa DÉCLARATION, jamais sa valeur — le producteur (Console) et les trois
 * lecteurs (back-office serveur, back-office web, SQL comptable) l'écrivaient chacun de leur côté.
 *
 * ⚠️ 🔴 À NE PAS CONFONDRE AVEC `signature_algo`, ET CE N'EST PAS LA MÊME QUESTION. Ce préfixe répond
 * à « **ce REGISTRE est-il une reprise ?** » — ce qui décide de ce qu'on a le droit d'en faire à
 * l'écran (on ne justifie pas l'écart d'une journée reprise). L'algorithme de signature répond à
 * « **cette PIÈCE est-elle signée par nous ?** » — ce qui décide si elle entre dans un verdict de
 * chaîne. Les deux coïncident aujourd'hui, et les unifier serait une erreur : le jour où une pièce
 * arrive sur un registre de caisse avec un algorithme que nous ne produisons pas, c'est la seconde
 * question qui doit la sortir du verdict, pas la première.
 */
export const LEGACY_REGISTER_PREFIX = 'LEGACY-';

/**
 * Le registre de reprise d'une caisse de l'ancien logiciel.
 * ⚠️ UN SEUL PRODUCTEUR, et il vit ici avec le lecteur : écrits séparément, l'un aurait pu changer de
 * forme sans l'autre, et la reprise serait devenue invisible aux écrans qui la reconnaissent.
 */
export const legacyRegisterId = (caisse: unknown): string =>
  `${LEGACY_REGISTER_PREFIX}C${String(caisse ?? '').replace(/\D/g, '') || '0'}`;

/**
 * Ce registre vient-il d'une reprise ?
 * ⚠️ La sentinelle est le `register_id` LUI-MÊME, pas une colonne ajoutée : on lit ce qui est déjà
 * écrit dans la pièce scellée.
 */
export const isLegacyRegister = (registerId: unknown): boolean =>
  String(registerId ?? '').startsWith(LEGACY_REGISTER_PREFIX);

/** Numéro suivant d'une séquence continue (dernier numéro persisté → +1, sans trou) */
export function nextSequence(lastNumber: number): number {
  if (!Number.isInteger(lastNumber) || lastNumber < 0) throw new Error(`core-nf525: dernier numéro de séquence invalide (${lastNumber})`);
  return lastNumber + 1;
}

/**
 * Numéro de pièce COURT : `<PRÉFIXE>-S<magasin>R<caisse>-<numéro>` (ex. `T-S1R1-000312`).
 * ⚠️ `magasin` = CODE COURT (numéro de magasin configuré au back-office, unique) — PAS le GLN,
 * qui reste porté par la pièce pour l'identité fiscale (SPEC §30.7.1). But : un numéro LISIBLE à
 * l'écran + scannable (code-barres de rappel), sans les 13 chiffres du GLN, et sans chevauchement
 * entre magasins. `magasin`/`caisse` = ENTIERS (chiffres uniquement) → `S12R3` se relit sans
 * ambiguïté (le « R » délimite). Le numéro séquentiel est zéro-paddé à 6 mais DÉBORDE sans
 * rebouclage au-delà de 999999 (la continuité prime sur la largeur).
 */
export function formatPieceNumber(kind: SequenceKind, storeCode: string | number, registerId: string | number, seq: number): string {
  if (!Number.isInteger(seq) || seq < 1) throw new Error(`core-nf525: numéro séquentiel invalide (${seq})`);
  if (!/^\d+$/.test(String(storeCode)) || !/^\d+$/.test(String(registerId))) {
    throw new Error('core-nf525: code magasin/caisse = entiers (chiffres uniquement) pour un numéro court non ambigu');
  }
  return `${SEQUENCE_PREFIXES[kind]}-S${storeCode}R${registerId}-${String(seq).padStart(6, '0')}`;
}

/**
 * Ce numéro est-il dans la SÉRIE DES FACTURES (`F-`, ou l'ancien `FT-`) ? — la facture et la facture d'avoir s'y
 * numérotent (Partie 10, CGI art. 242 nonies A). Un numéro illisible n'en est pas.
 */
export function isInvoiceSeriesNumber(pieceNumber: string): boolean {
  // ⚠️ LE PRÉFIXE SEUL : il dit la série, quel que soit le format de la suite (le format court a déjà changé une fois).
  const m = /^([A-Z]+)-/.exec(pieceNumber);
  if (!m) return false;
  return m[1] === SEQUENCE_PREFIXES.facture || (FORMER_SEQUENCE_PREFIXES.facture ?? []).includes(m[1]!);
}

/** Ré-extrait (préfixe, code magasin, caisse, numéro séquentiel) d'un numéro de pièce court `T-S1R1-000312`. */
export function parsePieceNumber(pieceNumber: string): { prefix: string; storeCode: string; registerId: string; seq: number } {
  const m = /^([A-Z]+)-S(\d+)R(\d+)-(\d+)$/.exec(pieceNumber);
  if (!m) throw new Error(`core-nf525: numéro de pièce illisible ${JSON.stringify(pieceNumber)}`);
  return { prefix: m[1] as string, storeCode: m[2] as string, registerId: m[3] as string, seq: parseInt(m[4] as string, 10) };
}

/**
 * Contrôle de continuité d'une séquence (Inspecteur NF525, JET 90) :
 * la liste triée des numéros doit être strictement consécutive.
 * Retourne les numéros MANQUANTS (trous) et les DOUBLONS.
 */
export function checkContinuity(numbers: number[]): { ok: boolean; missing: number[]; duplicates: number[] } {
  const sorted = [...numbers].sort((a, b) => a - b);
  const missing: number[] = [];
  const duplicates: number[] = [];
  for (let i = 1; i < sorted.length; i++) {
    const prev = sorted[i - 1] as number;
    const cur = sorted[i] as number;
    if (cur === prev) duplicates.push(cur);
    else for (let g = prev + 1; g < cur; g++) missing.push(g);
  }
  return { ok: missing.length === 0 && duplicates.length === 0, missing, duplicates };
}
